import * as React from 'react';

import classnames from 'classnames';

import styles from './body.module.css';

export type TextSize = 'mini' | 'tiny' | 'small' | 'normal' | 'large' | 'xlarge';

export type Color =
    | 'light'
    | 'medium'
    | 'dark'
    | 'person'
    | 'error'
    | 'blue'
    | 'white'
    | 'green'
    | 'blue-dk'
    | 'navy-dk'
    | 'plum'
    | 'ai-blue'
    | 'warn'
    | 'alert'
    | 'placeholder'
    | 'inherit'
    | 'ai-purple'
    | 'ai-purple-dk';

type TruncateBlock = {
    type: 'block';
    maxLines?: number; // Defaults to 1 if not provided
};

type TruncateInline = {
    type: 'inline';
};

export type Truncate = TruncateBlock | TruncateInline;

export type Props = {
    children?: React.ReactNode;
    as?: string;
    size?: TextSize;
    color?: Color;
    isBold?: boolean;
    isItalic?: boolean;
    truncate?: Truncate;
    getBodyRef?: any;
    title?: string;
    underline?: boolean;
    strikethrough?: boolean;
    nowrap?: boolean;
    wrapAnywhere?: boolean;
    maintainLineBreaks?: boolean;
    preventCopy?: boolean;
    useDoyle?: boolean;
    isFullWidth?: boolean;
    textAlign?: 'left' | 'center' | 'right';
    style?: any;
};

export function Body(props: Props) {
    const {
        as = 'span',
        size = 'normal',
        color = 'medium',
        isBold,
        isItalic,
        underline,
        strikethrough,
        truncate,
        getBodyRef,
        title,
        nowrap,
        wrapAnywhere,
        maintainLineBreaks,
        preventCopy,
        useDoyle,
        isFullWidth,
        textAlign,
        style,
    } = props;

    const Tag = truncate && truncate.type === 'block' ? 'div' : as;

    const styleNames = classnames({
        [styles.body]: true,
        [styles.mini]: size === 'mini',
        [styles.tiny]: size === 'tiny',
        [styles.small]: size === 'small',
        [styles.normal]: size === 'normal',
        [styles.large]: size === 'large',
        [styles.xlarge]: size === 'xlarge',
        [styles.light]: color === 'light',
        [styles.medium]: color === 'medium',
        [styles.dark]: color === 'dark',
        [styles.person]: color === 'person',
        [styles.error]: color === 'error',
        [styles.blue]: color === 'blue',
        [styles.white]: color === 'white',
        [styles.green]: color === 'green',
        [styles.placeholder]: color === 'placeholder',
        [styles.plum]: color === 'plum',
        [styles['blue-dk']]: color === 'blue-dk',
        [styles['navy-dk']]: color === 'navy-dk',
        [styles['ai-blue']]: color === 'ai-blue',
        [styles['ai-purple']]: color === 'ai-purple',
        [styles['ai-purple-dk']]: color === 'ai-purple-dk',
        [styles.warn]: color === 'warn',
        [styles.alert]: color === 'alert',
        [styles.inherit]: color === 'inherit',
        [styles.bold]: isBold,
        [styles.italic]: isItalic,
        [styles.underline]: underline,
        [styles.strikethrough]: strikethrough,
        [styles['truncate-block']]: truncate && truncate.type === 'block',
        [styles.nowrap]: nowrap,
        [styles['wrap-anywhere']]: wrapAnywhere,
        [styles['truncate-inline']]: truncate && truncate.type === 'inline',
        [styles['maintain-line-breaks']]: maintainLineBreaks,
        [styles['prevent-copy']]: preventCopy,
        [styles.doyle]: useDoyle,
        [styles['full-width']]: isFullWidth,
        [styles['text-align-left']]: textAlign === 'left',
        [styles['text-align-center']]: textAlign === 'center',
        [styles['text-align-right']]: textAlign === 'right',
    });

    const maxLines = truncate ? getMaxLines(truncate) : undefined;

    return (
        // @ts-expect-error
        <Tag
            ref={getBodyRef}
            title={title}
            style={{
                ...style,
                WebkitLineClamp: maxLines,
                lineClamp: maxLines,
            }}
            className={styleNames}
        >
            {props.children}
        </Tag>
    );
}

function getMaxLines(truncate: Truncate): number | null | undefined {
    if (truncate && truncate.type === 'block') {
        return truncate.maxLines || 1;
    }

    return undefined;
}
